import type { TreeCluster } from '../api/types'
import { BookOpen01, Briefcase01, ChevronDown, ChevronRight, CpuChip01, Dataflow03, File02, File06, Home02, Inbox01, List, MessageChatSquare, Plus, SearchLg, Settings04, Users01 } from '@untitledui/icons'
import { Focusable } from 'react-aria-components'
import { NavLink, useLocation, useNavigate, useParams } from 'react-router'
import { Avatar } from '@/components/base/avatar/avatar'
import { Tooltip, TooltipTrigger } from '@/components/base/tooltip/tooltip'
import { canSeeAgentJobs } from '@/pages/app/agent-jobs-gate'
import { canSeeWork } from '@/pages/app/work-page'
import { cx } from '@/utils/cx'
import { useShell } from '../store'
import { AccountMenu } from './account-menu'
import { isAgentsGroupPath } from './agents-group'
import { ArchivedClientsNode } from './archived-clients-node'
import { CountBadge } from './badge'
import { BrandMark } from './brand-mark'

const ROLE_LABEL: Record<string, string> = { cfo: 'CFO', controller: 'Controller', member: 'Staff accountant', viewer: 'Viewer', tax: 'Tax' }
/** PRD-020 §3.4 (QA-20): short tree tag for a file's role; operating (the common case) carries none. */
const FILE_ROLE_TAG: Record<string, string> = { holding: 'HOLD', management: 'MGMT', other: 'OTHER' }

/**
 * PRD-018 §3.1: Account → FIRM rows → CLIENTS → files, three levels, built from the bootstrap and
 * nothing else. The owning client of the current page cannot be collapsed.
 *
 * Markup is what it is (audit P2-1): a `nav` of lists of links with `aria-current="page"` on the active
 * row and a labeled disclosure button per client. No ARIA tree role, because these rows navigate and a
 * tree's arrow-key contract is not what they do. Tab moves between rows; Enter follows.
 *
 * PRD-026 v0.3 §3.20 (Sean 2026-09-20 21:14): a fixed section sits above the Clients tree (Skills, Logic / Method,
 * Definitions; Improvements joins with SI-10). Train 8 (Sean 2026-09-23): the header reads **Agents** (it was
 * "Skills & Logic") and the jobs row reads **Jobs**; the collapsed 48 px rail carries one icon for the group with a
 * fly-out of its rows, plus one initials chip per client. Prior art: Linear's fixed workspace items, Slack's rail.
 *
 * PRD-016 §5 v2.6.2 (Seb UX review note 50, decision 9): Agent jobs is the fourth row of this section, after
 * Skills · Method · Definitions — Skills says what agents can do, Method how, Definitions from which facts, Agent
 * jobs what they did, one subject. CFO and Controller only; a Staff accountant's agent work reaches them through
 * My work instead (a question or a result to accept is a work item), so the row and its badge are absent for
 * them, not disabled — there is nothing behind it for that role (audit P1-4: no control renders only to refuse).
 */
export function Tree() {
  const { bootstrap, treeCollapsed, toggleTree, collapsedClusters, toggleCluster, setPaletteOpen, setNewClientOpen, preview } = useShell()
  const { clusterId: activeCluster } = useParams()
  const navigate = useNavigate()
  const { me, tree, capabilities } = bootstrap
  const rowH = 'h-[var(--shell-row-height)]'
  // PRD-026 v0.2 §8 Q20: partners read Logic as Method; operators author it as Logic.
  const logicLabel = bootstrap.operator ? 'Logic' : 'Method'
  // PRD-016 §5 v2.6.2: Agent jobs is CFO and Controller only (one predicate with the route gate, the tab and the block).
  const showAgentJobs = canSeeAgentJobs(me.role)
  // PRD-018 v0.4 §3.18: Work (the firm-wide list) is a FIRM row beside My work for the CFO and Controller.
  const showWork = canSeeWork(me.role)
  // Train 8: the collapsed rail's one Agents icon lights up on any of the group's routes.
  const { pathname } = useLocation()
  const agentsGroupActive = isAgentsGroupPath(pathname)

  if (treeCollapsed) {
    return (
      <nav aria-label="Workspace" data-testid="tree" data-collapsed="true" className="flex h-full w-[var(--shell-rail-width)] flex-col items-center gap-1 border-r border-secondary bg-secondary py-3">
        <Tooltip title="Expand tree (⌘\)" placement="right">
          <TooltipTrigger aria-label="Expand tree" onPress={toggleTree} className="mb-2 mt-1 flex h-[var(--shell-row-height)] w-[var(--shell-row-height)] items-center justify-center rounded-md text-quaternary hover:bg-primary_hover"><ChevronRight className="size-4" /></TooltipTrigger>
        </Tooltip>
        <ul className="flex flex-col items-center gap-1">
          <li><RailLink to="/home" label="Home"><Home02 className="size-4" /></RailLink></li>
          <li><RailLink to="/my-work" label={`My work${bootstrap.counts.myWork ? ` (${bootstrap.counts.myWork})` : ''}`}><Inbox01 className="size-4" /></RailLink></li>
          {showWork ? <li><RailLink to="/work" label="Work" testId="rail-work"><List className="size-4" /></RailLink></li> : null}
          <li><RailLink to="/team" label="Team"><Users01 className="size-4" /></RailLink></li>
        </ul>
        <div className="my-2 h-px w-8 bg-border-secondary" />
        <ul className="flex flex-col items-center gap-1" aria-label="Agents" data-testid="rail-agents-group">
          {/* Train 8: one icon for the Agents group; its rows open in the fly-out (same pattern as the client chips below). */}
          <li className="group relative">
            <RailLink to="/firm/skills" label={`Agents${showAgentJobs && bootstrap.counts.threadsWaiting ? ` (${bootstrap.counts.threadsWaiting} stopped on you)` : ''}`} testId="rail-agents" activeWhen={agentsGroupActive}><CpuChip01 className="size-4" /></RailLink>
            <div className="absolute left-full top-0 z-20 hidden min-w-48 rounded-lg border border-secondary bg-primary p-1 shadow-lg group-hover:block group-focus-within:block" data-testid="rail-agents-flyout">
              <div className="px-2 py-1 text-xs font-semibold text-quaternary">Agents</div>
              <ul>
                <li><NavLink to="/firm/skills" data-testid="rail-skills" className="block rounded px-2 py-1 text-sm text-secondary hover:bg-primary_hover">Skills</NavLink></li>
                <li><NavLink to="/firm/logic" data-testid="rail-logic" className="block rounded px-2 py-1 text-sm text-secondary hover:bg-primary_hover">{logicLabel}</NavLink></li>
                <li><NavLink to="/firm/definitions" data-testid="rail-definitions" className="block rounded px-2 py-1 text-sm text-secondary hover:bg-primary_hover">Definitions</NavLink></li>
                {showAgentJobs
                  ? (
                      <li>
                        <NavLink to="/agent-jobs" data-testid="rail-agent-jobs" className="flex items-center gap-2 rounded px-2 py-1 text-sm text-secondary hover:bg-primary_hover">
                          <span>Jobs</span>
                          {bootstrap.counts.threadsWaiting > 0 ? <CountBadge badge={{ kind: 'hot', count: bootstrap.counts.threadsWaiting }} quiet={0} label="Jobs" /> : null}
                        </NavLink>
                      </li>
                    )
                  : null}
              </ul>
            </div>
          </li>
        </ul>
        <div className="my-2 h-px w-8 bg-border-secondary" />
        <ul className="flex flex-col items-center gap-1">
          {tree.map(t => (
            <li key={t.cluster.id} className="group relative">
              <RailLink to={`/c/${t.cluster.id}`} label={t.cluster.name} testId="rail-client"><span className="text-[11px] font-semibold tracking-wide" aria-hidden="true">{initials(t.cluster.name)}</span></RailLink>
              <div className="absolute left-full top-0 z-20 hidden min-w-48 rounded-lg border border-secondary bg-primary p-1 shadow-lg group-hover:block group-focus-within:block">
                <div className="px-2 py-1 text-xs font-semibold text-quaternary">{t.cluster.name}</div>
                <ul>
                  {t.files.map(f => <li key={f.id}><NavLink to={`/c/${t.cluster.id}/f/${f.id}`} className="block rounded px-2 py-1 text-sm text-secondary hover:bg-primary_hover">{f.name}</NavLink></li>)}
                </ul>
              </div>
            </li>
          ))}
        </ul>
      </nav>
    )
  }

  return (
    <nav aria-label="Workspace" data-testid="tree" className="flex h-full w-[var(--shell-tree-width)] flex-col border-r border-secondary bg-secondary">
      <div className="flex items-center gap-2 px-4 pt-4 pb-2">
        {/* PRD-018 §3.5: the logo is one of the three Home redirects (with `g h` and the palette). */}
        <NavLink to="/home" aria-label="Gallop, go to the start page" title="Home" data-testid="logo-home" className="inline-flex size-7 shrink-0 items-center justify-center rounded-md outline-none hover:opacity-90 focus-visible:ring-2 focus-visible:ring-[var(--shell-focus-ring)]">
          <BrandMark className="size-7" />
        </NavLink>
        <span className="truncate text-sm font-semibold text-primary" data-testid="account-name">{bootstrap.account.name}</span>
        <Tooltip title="Collapse tree (⌘\)" placement="right">
          <TooltipTrigger aria-label="Collapse tree" onPress={toggleTree} className="ml-auto flex size-7 shrink-0 items-center justify-center rounded-md text-quaternary hover:bg-primary_hover"><ChevronRight className="size-4 rotate-180" /></TooltipTrigger>
        </Tooltip>
      </div>
      <button type="button" onClick={() => setPaletteOpen(true)} className={cx(rowH, 'mx-3 mb-2 flex items-center gap-2 rounded-md border border-secondary bg-primary px-2 text-sm text-quaternary hover:bg-primary_hover')}>
        <SearchLg className="size-4" />
        <span>Jump to…</span>
        <kbd className="ml-auto rounded border border-secondary px-1 text-xs">⌘K</kbd>
      </button>
      <div className="flex-1 overflow-y-auto px-2 pb-2">
        <Section label="Firm">
          <li><Row to="/home" icon={<Home02 className="size-4" />} label="Home" /></li>
          <li><Row to="/my-work" icon={<Inbox01 className="size-4" />} label="My work" trailing={bootstrap.counts.myWork > 0 ? <CountBadge badge={{ kind: 'hot', count: bootstrap.counts.myWork }} quiet={0} label="My work" /> : null} /></li>
          {/* PRD-018 v0.4 §3.18 (Seb UX review note 28, decision 1): the firm-wide Work list, CFO and Controller; no badge (it is a view, not a queue). */}
          {showWork ? <li><Row to="/work" icon={<List className="size-4" />} label="Work" /></li> : null}
          <li><Row to="/team" icon={<Users01 className="size-4" />} label="Team" /></li>
          {preview ? <PreviewRows /> : null}
        </Section>
        {/* PRD-026 v0.3 §3.20: the fixed section above the client tree. Skills (the catalog), Logic (PRD-026 §3.1, the firm library every client inherits), Definitions (v0.2, its first nav row). Train 8: the header reads Agents. */}
        <Section label="Agents" testId="tree-section-agents">
          <li><Row to="/firm/skills" end={false} icon={<Dataflow03 className="size-4" />} label="Skills" /></li>
          <li><Row to="/firm/logic" end={false} icon={<BookOpen01 className="size-4" />} label={logicLabel} /></li>
          <li><Row to="/firm/definitions" end={false} icon={<Settings04 className="size-4" />} label="Definitions" /></li>
          {/* PRD-016 §5 v2.6.2 (Seb UX review note 50, decision 9): the jobs row is the fourth row of this section
              (what the agents did, after what they can do, how, and from which facts), CFO and Controller only;
              the badge is jobs stopped on this person (PRD-025 slice 2b). Train 8: under the Agents header the row
              reads "Jobs"; the page title and route (/agent-jobs) keep "Agent jobs". */}
          {showAgentJobs
            ? <li><Row to="/agent-jobs" end={false} icon={<MessageChatSquare className="size-4" />} label="Jobs" trailing={bootstrap.counts.threadsWaiting > 0 ? <CountBadge badge={{ kind: 'hot', count: bootstrap.counts.threadsWaiting }} quiet={0} label="Jobs" /> : null} /></li>
            : null}
        </Section>
        <Section label="Clients">
          {tree.length === 0 ? <li className="px-3 py-2 text-sm text-quaternary">No clients yet</li> : null}
          {tree.map(t => (
            <ClusterRows
              key={t.cluster.id}
              t={t}
              pinnedOpen={t.cluster.id === activeCluster}
              collapsed={collapsedClusters.has(t.cluster.id) && t.cluster.id !== activeCluster}
              onToggle={() => toggleCluster(t.cluster.id)}
              showAddFile={capabilities.canConnectFile}
              /* The client page owns the Add file chooser (PR #44); `?add=file` asks it to open on arrival and is
                 stripped there. Before this the row went to `?connect=1`, which ConnectNotice read as a failed return. */
              onAddFile={() => navigate(`/c/${t.cluster.id}?add=file`)}
            />
          ))}
          {capabilities.canCreateClient
            ? (
                <li>
                  <button type="button" onClick={() => setNewClientOpen(true)} className={cx(rowH, 'flex w-full items-center gap-2 rounded-md px-3 text-sm text-quaternary hover:bg-primary_hover')} data-testid="tree-new-client">
                    <Plus className="size-4" />
                    New client
                  </button>
                </li>
              )
            : null}
          {/* PRD-020 §3.14: archived clients sit under a collapsed node at the bottom; CFO and Controllers (the reader roles) see it. */}
          <ArchivedClientsNode enabled={me.role === 'cfo' || me.role === 'controller'} rowClass={rowH} />
        </Section>
      </div>
      <div className="border-t border-secondary p-2">
        <AccountMenu label={`Account menu for ${me.name}`} testId="tree-account-menu" className="flex w-full items-center gap-2 rounded-md p-1 text-left outline-none hover:bg-primary_hover focus-visible:ring-2 focus-visible:ring-[var(--shell-focus-ring)]">
          <Avatar size="sm" initials={initials(me.name)} alt="" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-primary" data-testid="me-name">{me.name}</span>
            <span className="block truncate text-xs text-quaternary" data-testid="me-role">{ROLE_LABEL[me.role] ?? me.role}</span>
          </span>
        </AccountMenu>
      </div>
    </nav>
  )
}

function PreviewRows() {
  return (
    <>
      {['Schedule', 'Usage', 'Credits balance'].map(n => (
        <li key={n} className="flex h-[var(--shell-row-height)] items-center gap-2 rounded-md px-3 text-sm text-quaternary">
          <span>{n}</span>
          <span className="ml-auto rounded bg-secondary px-1 text-[10px] font-semibold uppercase text-quaternary">R1</span>
        </li>
      ))}
    </>
  )
}

function Section({ label, children, testId }: { label: string, children: React.ReactNode, testId?: string }) {
  const id = `tree-section-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
  return (
    <div className="mb-3" data-testid={testId}>
      <div id={id} className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-quaternary">{label}</div>
      <ul aria-labelledby={id}>{children}</ul>
    </div>
  )
}

function Row({ to, icon, label, trailing, depth = 0, muted = false, ancestor = false, end = true, books, badgeTag }: { to: string, icon: React.ReactNode, label: string, trailing?: React.ReactNode, depth?: number, muted?: boolean, ancestor?: boolean, end?: boolean, books?: 'qbo' | 'none', /** QA-20: a small text tag next to the label (the file's role, when it isn't the common one). */ badgeTag?: string }) {
  return (
    <NavLink
      to={to}
      end={end}
      style={{ paddingLeft: `${12 + depth * 16}px` }}
      className={({ isActive }) => cx(
        'flex h-[var(--shell-row-height)] items-center gap-2 rounded-md pr-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-[var(--shell-focus-ring)]',
        isActive ? 'bg-brand-primary text-brand-secondary font-medium' : ancestor ? 'text-primary font-medium hover:bg-primary_hover' : muted ? 'text-quaternary hover:bg-primary_hover' : 'text-secondary hover:bg-primary_hover',
      )}
      data-testid="tree-row"
      data-books={books}
    >
      {({ isActive }) => (
        <>
          <span className={cx('shrink-0', isActive ? 'text-current' : 'text-quaternary')}>{icon}</span>
          <span className="truncate">{label}</span>
          {badgeTag ? <span className="shrink-0 rounded bg-secondary px-1 text-[10px] font-semibold text-quaternary" data-testid="tree-row-role">{badgeTag}</span> : null}
          {trailing}
        </>
      )}
    </NavLink>
  )
}

function ClusterRows({ t, collapsed, pinnedOpen, onToggle, showAddFile, onAddFile }: { t: TreeCluster, collapsed: boolean, pinnedOpen: boolean, onToggle: () => void, showAddFile: boolean, onAddFile: () => void }) {
  const { fileId } = useParams()
  const filesId = `tree-files-${t.cluster.id}`
  return (
    <li data-testid={`cluster-${t.cluster.id}`}>
      <div className="flex items-center">
        <button type="button" aria-label={collapsed ? `Expand ${t.cluster.name}` : `Collapse ${t.cluster.name}`} aria-expanded={!collapsed} aria-controls={filesId} disabled={pinnedOpen} onClick={onToggle} className="ml-0.5 flex size-6 shrink-0 items-center justify-center rounded text-quaternary hover:bg-primary_hover disabled:opacity-40 disabled:hover:bg-transparent">
          {collapsed ? <ChevronRight className="size-3.5" /> : <ChevronDown className="size-3.5" />}
        </button>
        <div className="min-w-0 flex-1">
          <Row to={`/c/${t.cluster.id}`} end={false} icon={<Briefcase01 className="size-4" />} label={t.cluster.name} ancestor={!!fileId} trailing={<CountBadge badge={t.cluster.badge} quiet={t.cluster.quietCount} label={t.cluster.name} />} />
        </div>
      </div>
      {!collapsed
        ? (
            <ul id={filesId} className="ml-4 border-l border-secondary">
              {t.files.map(f => (
                <li key={f.id}>
                  {/* QA-27: a paper entity (books = none) draws a plain outlined document, a QuickBooks file the lined one. Same row, different glyph. */}
                  <Row
                    to={`/c/${t.cluster.id}/f/${f.id}`}
                    end={false}
                    depth={1}
                    icon={f.books === 'none' ? <File02 className="size-4" aria-label="Paper entity" /> : <File06 className="size-4" />}
                    label={f.name}
                    muted={!f.inScope}
                    books={f.books}
                    /* QA-20: a small tag for the uncommon roles; operating (the default, most files) carries none. */
                    badgeTag={f.role !== 'operating' ? FILE_ROLE_TAG[f.role] : undefined}
                    trailing={f.inScope ? <CountBadge badge={f.badge} quiet={f.quietCount} label={f.name} /> : null}
                  />
                </li>
              ))}
              {showAddFile
                ? (
                    <li>
                      <button type="button" onClick={onAddFile} aria-label={`Add file to ${t.cluster.name}`} data-testid="tree-add-file" style={{ paddingLeft: '28px' }} className="flex h-[var(--shell-row-height)] w-full items-center gap-2 rounded-md pr-2 text-sm text-quaternary outline-none hover:bg-primary_hover focus-visible:ring-2 focus-visible:ring-[var(--shell-focus-ring)]">
                        <Plus className="size-4" />
                        Add file
                      </button>
                    </li>
                  )
                : null}
            </ul>
          )
        : null}
    </li>
  )
}

function RailLink({ to, label, children, testId, activeWhen }: { to: string, label: string, children: React.ReactNode, testId?: string, /** Overrides the link's own active match (a group icon is active on any of its rows' routes). */ activeWhen?: boolean }) {
  return (
    <Tooltip title={label} placement="right">
      <Focusable>
        <NavLink to={to} aria-label={label} data-testid={testId} className={({ isActive }) => cx('flex h-[var(--shell-row-height)] w-[var(--shell-row-height)] items-center justify-center rounded-md outline-none focus-visible:ring-2 focus-visible:ring-[var(--shell-focus-ring)]', (activeWhen ?? isActive) ? 'bg-brand-primary text-brand-secondary' : 'text-quaternary hover:bg-primary_hover')}>{children}</NavLink>
      </Focusable>
    </Tooltip>
  )
}

export function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]!.toUpperCase()).join('')
}
